import {
  FUTURE_SECURITY_KEY_FIELD,
  FUTURE_SECURITY_SEARCH_FIELDS,
  IFutureSecurity,
} from "@/lib/interfaces and Enums/ENUMFutureSecurity";
import {
  DEFAULT_SEARCH_LIMIT,
  ISearchResult,
  findItemByFieldValue,
  searchItemsByFields,
} from "@/lib/referenceDataSearchHelper";
import futureSecuritiesStore from "./futureSecuritiesStore";
import { loadReferenceDataOnce } from "./referenceDataLoadHelper";

// LQC-2493 - GET /Securities/Futures (LQC-2496), called on demand the first time a user
// selects Future in the ticket's instrument dropdown. Bond securities load with the
// widget; futures wait for the instrument, so a bond-only user never pays for them.

export const FUTURE_SECURITIES_ENDPOINT = "/Securities/Futures";

// ----- Pure transform -------------------------------------------------------

// Unwrap { futures: [...] }. Returns null for anything else so the loader can flag a
// contract mismatch rather than seeding an empty list.
export function extractFutureSecurities(
  response: any,
): IFutureSecurity[] | null {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.futures)) return response.futures;
  return null;
}

// ----- Feed -----------------------------------------------------------------

export async function getAllFutureSecurities(
  getAccessTokenSilently: Function,
  forceReload = false,
): Promise<void> {
  const store = futureSecuritiesStore.getState();
  await loadReferenceDataOnce<IFutureSecurity>({
    url: FUTURE_SECURITIES_ENDPOINT,
    source: "futureSecuritiesStoreACTIONS.getAllFutureSecurities",
    getAccessTokenSilently,
    readStatus: () => futureSecuritiesStore.getState().futureSecuritiesStatus,
    setStatus: store.setFutureSecuritiesStatus,
    setItems: store.setFutureSecurities,
    setError: store.setFutureSecuritiesError,
    extractItems: extractFutureSecurities,
    forceReload,
  });
}

// ----- Search / lookup ------------------------------------------------------

// Type-to-find over symbol, contract name and exchange, ranked by the shared helper.
export function searchFutureSecurities(
  term: string,
  limit: number = DEFAULT_SEARCH_LIMIT,
): ISearchResult<IFutureSecurity> {
  return searchItemsByFields<IFutureSecurity>({
    items: futureSecuritiesStore.getState().futureSecurities,
    term,
    fields: FUTURE_SECURITY_SEARCH_FIELDS,
    limit,
  });
}

// Confirms a chosen contract really exists in our data before it is sent.
export function findFutureSecurityBySymbol(
  bloombergSymbol: string,
): IFutureSecurity | undefined {
  return findItemByFieldValue<IFutureSecurity>(
    futureSecuritiesStore.getState().futureSecurities,
    FUTURE_SECURITY_KEY_FIELD,
    bloombergSymbol,
  );
}

// Validation gate for the send path - a symbol we cannot resolve must not reach the
// back end.
export function isKnownFutureSymbol(bloombergSymbol: string): boolean {
  return findFutureSecurityBySymbol(bloombergSymbol) !== undefined;
}
